/**
 * Game Playground Page
 * 
 * Phase 6.0 Track C: Local testing page for Wheel of Fortune
 * No Shopify/OAuth required
 */

import { useState, useEffect, useRef, useCallback } from "react";
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  Button,
  Checkbox,
  Text,
  Banner,
  Layout,
  Collapsible,
  Select,
  Divider,
  Box,
} from "@shopify/polaris";
import { PixiApp } from "../games/runtime/PixiApp";
import { WheelGame } from "../games/wheel_of_fortune/WheelGame";
import { loadGameManifest } from "../games/runtime/AssetLoader";
import { loadWheelTextures } from "../games/wheel_of_fortune/textureLoader";
import { loadIconTextures } from "../games/wheel_of_fortune/iconLoader";
import { WheelConfigEditor } from "../components/WheelConfigEditor";
import type { GameOutcome, GameState, WheelConfig, DebugOptions, GameManifestV1 } from "../games/runtime/types";

function GamePlaygroundPage() {
  const canvasRef = useRef<HTMLDivElement>(null);
  const pixiAppRef = useRef<PixiApp | null>(null);
  const wheelGameRef = useRef<WheelGame | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  const [gameState, setGameState] = useState<GameState>("idle");
  const [lastOutcome, setLastOutcome] = useState<GameOutcome | null>(null);
  const [mockRewardCode, setMockRewardCode] = useState<string | null>(null);
  const [manifest, setManifest] = useState<GameManifestV1 | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [debug, setDebug] = useState<DebugOptions>({
    showSegmentIndices: false,
    showStopAngle: false,
    logOutcome: true,
  });
  const [debugPanelOpen, setDebugPanelOpen] = useState(false);
  
  // Render scale for Hi-DPI rendering
  const [renderScale, setRenderScale] = useState<number>(window.devicePixelRatio || 2);
  
  // Quality baseline metrics
  const [qualityMetrics, setQualityMetrics] = useState<{
    canvasWidth: number;
    canvasHeight: number;
    rendererWidth: number;
    rendererHeight: number;
    resolution: number;
    devicePixelRatio: number;
    wheelRadius: number;
    actualCanvasSize: string;
  } | null>(null);

  // Wheel config state (editable) - 9 segments to match PNG (in-memory only; no browser persistence)
  const [wheelConfig, setWheelConfig] = useState<WheelConfig>(() => ({
      segments: [
        { outcomeKey: "discount_10", label: "10% Off", color: "#E63946", weight: 2, iconKey: "percent" },
        { outcomeKey: "discount_20", label: "20% Off", color: "#1D3557", weight: 1, iconKey: "diamond" },
        { outcomeKey: "free_shipping", label: "Free Ship", color: "#2A9D8F", weight: 2, iconKey: "shipping" },
        { outcomeKey: "try_again", label: "Try Again", color: "#E9C46A", weight: 3, iconKey: "nowin" },
        { outcomeKey: "discount_50", label: "50% Off!", color: "#E76F51", weight: 1, iconKey: "star" },
        { outcomeKey: "bonus_points", label: "100 Points", color: "#457B9D", weight: 2, iconKey: "gift" },
        { outcomeKey: "mystery", label: "Mystery", color: "#F4A261", weight: 2, iconKey: "gift" },
        { outcomeKey: "jackpot", label: "Jackpot!", color: "#264653", weight: 1, iconKey: "trophy" },
        { outcomeKey: "spin_again", label: "Spin Again", color: "#A8DADC", weight: 2, iconKey: "coin" },
      ],
      spinDuration: 4000,
      theme: "classic",
      ctaText: "Spin to Win!",
    }));

  // Update config from manifest when loaded
  useEffect(() => {
    if (manifest?.defaultConfig) {
      setWheelConfig(manifest.defaultConfig as unknown as WheelConfig);
    }
  }, [manifest]);

  /**
   * Load game manifest
   */
  useEffect(() => {
    const loadManifest = async () => {
      try {
        setGameState("loading");
        const loadedManifest = await loadGameManifest("wheel_of_fortune", "v1");
        setManifest(loadedManifest);
        console.log("[Playground] Manifest loaded:", loadedManifest);
      } catch (error) {
        console.error("[Playground] Failed to load manifest:", error);
        setLoadError(error instanceof Error ? error.message : "Failed to load manifest");
        // Continue with fallback config
        setManifest(null);
      }
    };

    loadManifest();
  }, []);

  /**
   * Initialize Pixi and Wheel Game (once on mount)
   */
  useEffect(() => {
    const initGame = async () => {
      if (!canvasRef.current || !wheelConfig) return;

      try {
        setGameState("loading");
        
        // 1. Preload all wheel textures (16 PNG layers)
        console.log('[Playground] Loading wheel textures...');
        const textures = await loadWheelTextures();
        console.log('[Playground] ✅ Textures loaded');
        
        // 1b. Load icon textures (for segment icons)
        console.log('[Playground] Loading icon textures...');
        const iconTextures = await loadIconTextures();
        console.log('[Playground] ✅ Icon textures loaded');
        
        // 2. Create Pixi app (Hi-DPI rendering, transparent background)
        const CANVAS_SIZE = 550;
        const pixiApp = new PixiApp();
        await pixiApp.init(canvasRef.current, {
          width: CANVAS_SIZE,
          height: CANVAS_SIZE,
          // backgroundAlpha defaults to 0 (transparent) in PixiApp
          resolution: renderScale, // Use configurable render scale
          antialias: true,
        });
        pixiAppRef.current = pixiApp;

        // 3. Create wheel game (with preloaded textures)
        const wheelGame = new WheelGame({
          config: wheelConfig,
          textures, // Preloaded PNG textures
          width: CANVAS_SIZE,
          height: CANVAS_SIZE,
          debug,
          onStateChange: (state) => {
            console.log("[Playground] State changed:", state);
            setGameState(state);
          },
          onOutcome: (outcome) => {
            console.log("[Playground] Outcome received:", outcome);
            setLastOutcome(outcome);
          },
        });
        wheelGameRef.current = wheelGame;
        
        // Pass icon textures to wheel renderer
        wheelGame.setIconTextures(iconTextures);

        const stage = pixiApp.getStage();
        if (stage) {
          stage.addChild(wheelGame);
        }

        // Register bulb animation ticker
        const bulbTicker = wheelGame.getBulbAnimationTicker();
        if (pixiApp.getApp() && bulbTicker) {
          pixiApp.getApp()!.ticker.add(bulbTicker);
        }

        // Start game loop
        const gameLoop = () => {
          if (wheelGame) {
            wheelGame.update(1 / 60); // Assuming 60fps
          }
          animationFrameRef.current = requestAnimationFrame(gameLoop);
        };
        gameLoop();

        // Collect quality metrics
        const app = pixiApp.getApp();
        if (app && app.renderer && app.canvas) {
          const wheelRadius = CANVAS_SIZE * 0.36; // From WheelGame.ts
          setQualityMetrics({
            canvasWidth: CANVAS_SIZE,
            canvasHeight: CANVAS_SIZE,
            rendererWidth: app.renderer.width,
            rendererHeight: app.renderer.height,
            resolution: app.renderer.resolution,
            devicePixelRatio: window.devicePixelRatio || 1,
            wheelRadius,
            actualCanvasSize: `${app.canvas.width}×${app.canvas.height}px`,
          });
          
          console.log("[Quality Baseline]", {
            logicalSize: `${CANVAS_SIZE}×${CANVAS_SIZE}`,
            rendererSize: `${app.renderer.width}×${app.renderer.height}`,
            actualCanvasSize: `${app.canvas.width}×${app.canvas.height}`,
            resolution: app.renderer.resolution,
            devicePixelRatio: window.devicePixelRatio,
            wheelRadius,
            wheelScale: wheelGameRef.current?.scale.x || 1,
          });
        }

        setGameState("ready");
        console.log("[Playground] Game initialized");
      } catch (error) {
        console.error("[Playground] Init error:", error);
        setGameState("error");
      }
    };

    initGame();

    // Cleanup
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      
      // Remove bulb animation ticker
      if (pixiAppRef.current && wheelGameRef.current) {
        const app = pixiAppRef.current.getApp();
        const bulbTicker = wheelGameRef.current.getBulbAnimationTicker();
        if (app && bulbTicker) {
          app.ticker.remove(bulbTicker);
        }
      }
      
      if (wheelGameRef.current) {
        // Don't destroy, just cleanup refs
        wheelGameRef.current = null;
      }
      if (pixiAppRef.current) {
        pixiAppRef.current.destroy();
        pixiAppRef.current = null;
      }
    };
  }, [renderScale]); // Re-init when render scale changes

  /**
   * Update debug options when they change
   */
  useEffect(() => {
    if (wheelGameRef.current) {
      wheelGameRef.current.updateDebug(debug);
    }
  }, [debug]);

  /**
   * Log config changes for debugging
   */
  useEffect(() => {
    console.log("[Playground] Config updated:", wheelConfig);
  }, [wheelConfig]);

  /**
   * Handle spin button click
   */
  const handleSpin = () => {
    if (gameState !== "ready") {
      console.warn("[Playground] Cannot spin in state:", gameState);
      return;
    }

    // Mock server outcome (random segment selection for testing)
    const randomSegmentIndex = Math.floor(Math.random() * wheelConfig.segments.length);
    const selectedSegment = wheelConfig.segments[randomSegmentIndex];

    // Calculate angles
    const totalWeight = wheelConfig.segments.reduce((sum, seg) => sum + seg.weight, 0);
    let cumulativeAngle = 0;
    let targetAngle = 0;

    for (let i = 0; i < wheelConfig.segments.length; i++) {
      const segment = wheelConfig.segments[i];
      const segmentAngle = (segment.weight / totalWeight) * 360;

      if (i === randomSegmentIndex) {
        targetAngle = cumulativeAngle + segmentAngle / 2;
        break;
      }

      cumulativeAngle += segmentAngle;
    }

    const baseRotations = 5;
    const totalRotation = baseRotations * 360 + targetAngle;

    const mockOutcome: GameOutcome = {
      outcomeKey: selectedSegment.outcomeKey,
      targetAngle,
      totalRotation,
      segmentLabel: selectedSegment.label,
      segmentColor: selectedSegment.color,
    };

    console.log("[Playground] Spinning with mock outcome:", mockOutcome);

    // Generate mock discount code (simulates backend)
    const mockCode = `GC-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
    setMockRewardCode(mockCode);

    if (wheelGameRef.current) {
      wheelGameRef.current.spin(mockOutcome);
    }
  };

  /**
   * Reset game
   */
  const handleReset = () => {
    if (wheelGameRef.current) {
      wheelGameRef.current.reset();
    }
    setLastOutcome(null);
    setMockRewardCode(null);
  };

  // Handle config changes
  const handleConfigChange = (newConfig: WheelConfig) => {
    setWheelConfig(newConfig);
    if (wheelGameRef.current) {
      wheelGameRef.current.updateConfig(newConfig);
    }
  };

  /**
   * Export current wheel as PNG
   */
  const handleExportPNG = useCallback(() => {
    const app = pixiAppRef.current?.getApp();
    if (!app || !app.stage) {
      console.error('[Export] No Pixi app available');
      return;
    }

    try {
      // Extract canvas as data URL
      const renderer = app.renderer;
      const canvas = renderer.extract.canvas(app.stage);
      
      // Check if canvas and toDataURL exist
      if (!canvas || typeof canvas.toDataURL !== 'function') {
        console.error('[Export] Canvas toDataURL not available');
        return;
      }
      
      const dataURL = canvas.toDataURL('image/png');

      // Create download link
      const link = document.createElement('a');
      link.download = `wheel-${Date.now()}.png`;
      link.href = dataURL;
      link.click();

      console.log('[Export] PNG downloaded');
    } catch (error) {
      console.error('[Export] Failed to export PNG:', error);
    }
  }, []);

  return (
    <Page
      title="Game Playground"
      subtitle="Local testing for Wheel of Fortune (no Shopify required)"
      backAction={{ content: "Dashboard", url: "/" }}
    >
      <BlockStack gap="400">
        {loadError && (
          <Banner tone="warning">
            <Text as="p">
              Failed to load manifest from server: {loadError}. Using fallback config.
            </Text>
          </Banner>
        )}

        <Layout>
          <Layout.Section variant="oneThird">
            {/* Config Editor */}
            <WheelConfigEditor config={wheelConfig} onChange={handleConfigChange} />
          </Layout.Section>

          <Layout.Section>
            <BlockStack gap="400">
              {/* Game Canvas */}
              <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Wheel of Fortune
              </Text>

              <div
                ref={canvasRef}
                style={{
                  width: "550px",
                  height: "550px",
                  border: "2px solid #e1e3e5",
                  borderRadius: "12px",
                  overflow: "hidden",
                  background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                }}
              />

              <InlineStack gap="200">
                <Button
                  onClick={handleSpin}
                  disabled={gameState !== "ready"}
                  variant="primary"
                >
                  {gameState === "playing" || gameState === "resolving"
                    ? "Spinning..."
                    : "Spin to Win!"}
                </Button>

                <Button onClick={handleReset} disabled={gameState === "idle"}>
                  Reset
                </Button>
              </InlineStack>

              <Text as="p" tone="subdued">
                State: <strong>{gameState}</strong>
              </Text>
            </BlockStack>
          </Card>

          {/* Quality Baseline - Hi-DPI Controls */}
          {qualityMetrics && (
            <Card>
              <BlockStack gap="400">
                <InlineStack align="space-between" blockAlign="center">
                  <Text as="h2" variant="headingMd">
                    Quality Baseline
                  </Text>
                  <Button onClick={handleExportPNG}>
                    Export PNG
                  </Button>
                </InlineStack>

                <BlockStack gap="300">
                  {/* Render Scale Control */}
                  <Select
                    label="Render Scale"
                    value={String(renderScale)}
                    options={[
                      { label: '1x (Standard)', value: '1' },
                      { label: '1.5x (Enhanced)', value: '1.5' },
                      { label: '2x (Retina)', value: '2' },
                      { label: '3x (Ultra)', value: '3' },
                    ]}
                    onChange={(value) => setRenderScale(parseFloat(value))}
                    helpText="Higher scale = sharper edges, larger file size"
                  />

                  <Divider />

                  {/* Read-only metrics */}
                  <InlineStack gap="400" wrap>
                    <Text as="p" variant="bodySm">
                      <strong>Logical:</strong> {qualityMetrics.canvasWidth}×{qualityMetrics.canvasHeight}px
                    </Text>
                    <Text as="p" variant="bodySm">
                      <strong>Actual Canvas:</strong> {qualityMetrics.actualCanvasSize}
                    </Text>
                    <Text as="p" variant="bodySm">
                      <strong>Resolution:</strong> {qualityMetrics.resolution}x
                    </Text>
                  </InlineStack>
                  
                  <InlineStack gap="400" wrap>
                    <Text as="p" variant="bodySm">
                      <strong>Device DPR:</strong> {qualityMetrics.devicePixelRatio}
                    </Text>
                    <Text as="p" variant="bodySm">
                      <strong>Wheel Radius:</strong> {qualityMetrics.wheelRadius.toFixed(1)}px
                    </Text>
                  </InlineStack>

                  <Text as="p" variant="bodySm" tone={
                    qualityMetrics.resolution >= 1.5
                      ? 'success' 
                      : 'subdued'
                  }>
                    <strong>Status:</strong> {
                      qualityMetrics.resolution >= 2
                        ? '✅ Retina quality'
                        : qualityMetrics.resolution >= 1.5
                        ? '✓ Enhanced quality'
                        : '⚠️ Standard quality'
                    }
                  </Text>
                </BlockStack>
              </BlockStack>
            </Card>
          )}

          {/* Debug Panel (Collapsible) */}
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="h2" variant="headingMd">
                  Debug Tools
                </Text>
                <Button
                  onClick={() => setDebugPanelOpen(!debugPanelOpen)}
                  disclosure={debugPanelOpen ? 'up' : 'down'}
                  size="slim"
                >
                  {debugPanelOpen ? 'Hide' : 'Show'}
                </Button>
              </InlineStack>

              <Collapsible
                open={debugPanelOpen}
                id="debug-panel"
                transition={{ duration: '200ms', timingFunction: 'ease-in-out' }}
              >
                <BlockStack gap="400">
                  <Checkbox
                    label="Show Segment Indices"
                    checked={debug.showSegmentIndices || false}
                    onChange={(checked) =>
                      setDebug({ ...debug, showSegmentIndices: checked })
                    }
                  />

                  <Checkbox
                    label="Show Stop Angle"
                    checked={debug.showStopAngle || false}
                    onChange={(checked) =>
                      setDebug({ ...debug, showStopAngle: checked })
                    }
                  />

                  <Checkbox
                    label="Log Outcome to Console"
                    checked={debug.logOutcome || false}
                    onChange={(checked) =>
                      setDebug({ ...debug, logOutcome: checked })
                    }
                  />

                  {lastOutcome && (
                    <BlockStack gap="200">
                      <Text as="h3" variant="headingSm">
                        Last Outcome
                      </Text>
                      <Text as="p" variant="bodySm">
                        <strong>Key:</strong> {lastOutcome.outcomeKey}
                      </Text>
                      <Text as="p" variant="bodySm">
                        <strong>Label:</strong> {lastOutcome.segmentLabel}
                      </Text>
                      <Text as="p" variant="bodySm">
                        <strong>Target Angle:</strong> {lastOutcome.targetAngle?.toFixed(2)}°
                      </Text>
                      <Text as="p" variant="bodySm">
                        <strong>Total Rotation:</strong> {lastOutcome.totalRotation?.toFixed(2)}°
                      </Text>
                      {mockRewardCode && (
                        <Box 
                          padding="300" 
                          background="bg-fill-success"
                          borderRadius="200"
                        >
                          <BlockStack gap="100">
                            <Text as="p" variant="bodySm" fontWeight="semibold">
                              🎉 Mock Discount Code:
                            </Text>
                            <Text as="p" variant="bodyLg" fontWeight="bold">
                              {mockRewardCode}
                            </Text>
                            <Text as="p" variant="bodySm" tone="subdued">
                              In production, this code would be created in Shopify
                            </Text>
                          </BlockStack>
                        </Box>
                      )}
                    </BlockStack>
                  )}
                </BlockStack>
              </Collapsible>
            </BlockStack>
          </Card>
            </BlockStack>
          </Layout.Section>
        </Layout>
      </BlockStack>
    </Page>
  );
}

export default GamePlaygroundPage;
